import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AIChatbot from './AIChatbot';

export default function ChatbotWidget({
  displayName = '',
  prefilledEmail = '',
  chatbotProps = {},
  fabBottom = 28,
  fabRight = 28,
  showFab = true,
  open: controlledOpen,
  onOpenChange,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const setOpen = (value) => {
    if (onOpenChange) onOpenChange(value);
    else setInternalOpen(value);
  };

  const toggle = () => {
    setOpen(!open);
    if (!open) setUnread(0);
  };

  return (
    <>
      {showFab ? (
      <motion.button
        onClick={toggle}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        title="AI Assistant"
        style={{
          position: 'fixed', bottom: fabBottom, right: fabRight, zIndex: 1000,
          width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: open
            ? '#EDF2F7'
            : 'linear-gradient(135deg, #3B5BDB 0%, #6366f1 100%)',
          boxShadow: open
            ? '0 2px 12px rgba(0,0,0,0.12)'
            : '0 6px 24px rgba(59,91,219,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.2s, box-shadow 0.2s',
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.svg
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.18 }}
              width="20" height="20" viewBox="0 0 24 24"
              fill="none" stroke="#1A202C" strokeWidth="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </motion.svg>
          ) : (
            <motion.span
              key="ai"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{ fontSize: 22, color: '#FFFFFF' }}
            >
              ✦
            </motion.span>
          )}
        </AnimatePresence>

        {/* Unread badge */}
        {!open && unread > 0 && (
          <div style={{
            position: 'absolute', top: 0, right: 0,
            width: 18, height: 18, borderRadius: '50%',
            background: '#ef4444', border: '2px solid #FFFFFF',
            fontSize: 10, fontWeight: 700, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {unread}
          </div>
        )}

        {/* Pulse ring (only when closed) */}
        {!open && (
          <motion.div
            animate={{ scale: [1, 1.6], opacity: [0.5, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
            style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: '#3B5BDB', zIndex: -1,
            }}
          />
        )}
      </motion.button>
      ) : null}

      {/* ── Chat panel ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="chatpanel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            style={{
              position: 'fixed', bottom: fabBottom + 64, right: fabRight, zIndex: 999,
              width: 420, height: 'min(620px, calc(100vh - 120px))', maxWidth: 'calc(100vw - 32px)',
              borderRadius: 20,
              overflow: 'hidden',
              boxShadow: '0 24px 64px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.1)',
              border: '1px solid #DDE1E7',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <AIChatbot
              displayName={displayName}
              prefilledEmail={prefilledEmail}
              {...chatbotProps}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
